import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { api, track } from "@/lib/api";
import { PLANNING_TOOLS_VISIBLE } from "@/lib/features";
import { Logo } from "@/components/site/Logo";

// Standard primary category order, used by the header, mobile menu and footer alike.
// Comfort & Sleep is temporarily hidden from the visitor experience (its pages and URLs are
// untouched); restore the entry below to bring the section back.
const HUBS = [
  { slug: "wellness-recovery", name: "Wellness & Recovery", short: "Wellness" },
  { slug: "outdoor-living", name: "Outdoor Living", short: "Outdoor Living" },
  { slug: "recreation", name: "Golf Simulators", short: "Golf Simulators" },
  { slug: "home-power-technology", name: "Home Power & Technology", short: "Home Power" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [banner, setBanner] = useState("");
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    api.get("/settings").then((r) => setBanner(r.data?.preview_banner || "")).catch(() => {});
  }, []);

  return (
    <>
      <a
        href="#main-content"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:left-4 focus:top-4 focus:bg-charcoal focus:text-white focus:px-5 focus:py-3 focus:text-[12.5px] focus:tracking-[0.14em] focus:uppercase"
      >
        Skip to content
      </a>
      {banner && (
        <div
          data-testid="preview-mode-banner"
          className="bg-charcoal text-[#E8E6E1] text-[12px] md:text-[12.5px] tracking-wide px-5 py-2.5 text-center"
        >
          <span className="uppercase tracking-[0.18em] text-[#A9B8A2] mr-2">Private preview</span>
          {banner}
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-line">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 flex items-center justify-between h-[68px] md:h-[80px] lg:h-[88px] xl:h-[96px]">
          <Logo />

          <nav aria-label="Primary" className="hidden lg:flex items-center lg:gap-6 xl:gap-8 text-[13.5px]">
            {HUBS.map((h) => (
              <NavLink
                key={h.slug}
                to={`/hub/${h.slug}`}
                data-testid={`nav-hub-${h.slug}`}
                className={({ isActive }) =>
                  `link-underline font-medium transition-colors ${isActive ? "text-sage" : "text-charcoal hover:text-sage"}`
                }
              >
                {h.short}
              </NavLink>
            ))}
            <NavLink
              to="/medical-mobility"
              data-testid="nav-medical-mobility"
              className={({ isActive }) =>
                `link-underline font-medium transition-colors ${isActive ? "text-sage" : "text-charcoal hover:text-sage"}`
              }
            >
              Medical &amp; Mobility
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            {PLANNING_TOOLS_VISIBLE && (
              <Link
                to="/tools"
                data-testid="header-tools-cta"
                className="hidden md:inline-flex items-center px-5 py-2.5 text-[12.5px] tracking-wide uppercase bg-charcoal text-white hover:bg-sage transition-colors duration-300"
              >
                Start Planning
              </Link>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              className="lg:hidden p-3 -mr-2 text-charcoal"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <nav
            id="mobile-nav"
            aria-label="Mobile"
            data-testid="mobile-nav"
            className="lg:hidden border-t border-line bg-white px-5 py-4"
          >
            {HUBS.map((h) => (
              <Link
                key={h.slug}
                to={`/hub/${h.slug}`}
                data-testid={`mobile-nav-${h.slug}`}
                className="block py-3.5 text-[15px] border-b border-line/70 font-light"
              >
                {h.name}
              </Link>
            ))}
            <Link
              to="/medical-mobility"
              data-testid="mobile-nav-medical-mobility"
              className="block py-3.5 text-[15px] border-b border-line/70 font-light"
            >
              Medical &amp; Mobility
            </Link>
            {PLANNING_TOOLS_VISIBLE && (
              <Link to="/tools" data-testid="mobile-nav-tools" className="block py-3.5 text-[15px] font-light">
                Planning Tools
              </Link>
            )}
            <Link to="/how-we-help" className="block py-3.5 text-[15px] font-light">
              How We Help
            </Link>
          </nav>
        )}
      </header>
    </>
  );
};

// The official Human Home Living profiles, in the approved display order. URLs are exact.
const SOCIALS = [
  {
    platform: "YouTube",
    href: "https://www.youtube.com/@humanhomeliving",
    path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  },
  {
    platform: "Instagram",
    href: "https://www.instagram.com/humanhomeliving/",
    path: "M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.9.419.418.679.815.896 1.378.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm7.846-10.405a1.441 1.441 0 0 1-2.88 0 1.44 1.44 0 0 1 2.88 0z",
  },
  {
    platform: "Pinterest",
    href: "https://www.pinterest.com/humanhomeliving/",
    path: "M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.734-1.377l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.124.347 2.317.535 3.554.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z",
  },
];

const SocialLinks = () => {
  const { pathname } = useLocation();
  return (
    <div className="mt-7" data-testid="footer-social">
      <div className="text-[10.5px] tracking-[0.18em] uppercase text-[#B7C6B0]">
        Follow Human Home Living
      </div>
      <div className="flex items-center gap-3 mt-3">
        {SOCIALS.map((s) => (
          <a
            key={s.platform}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            title={s.platform}
            aria-label={`Human Home Living on ${s.platform}`}
            data-testid={`footer-social-${s.platform.toLowerCase()}`}
            onClick={() =>
              track({
                event_type: "social_click",
                platform: s.platform,
                destination: s.href,
                cta_placement: "FOOTER",
                page: pathname,
              })
            }
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-[#DAD8D3] hover:text-charcoal hover:bg-[#B7C6B0] hover:border-[#B7C6B0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B7C6B0] focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal transition-colors duration-300"
          >
            <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
              <path d={s.path} />
            </svg>
          </a>
        ))}
      </div>
    </div>
  );
};

export const Footer = () => (
  <footer className="bg-charcoal text-[#DAD8D3] mt-24">
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20">
      <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 md:gap-12">
        <div>
          <div className="display text-2xl text-white">Human Home Living</div>
          <div
            data-testid="footer-tagline"
            className="text-[11px] md:text-[11.5px] tracking-[0.18em] uppercase text-[#B7C6B0] mt-2.5"
          >
            Smarter choices that fit your home and you
          </div>
          <p className="text-[15px] leading-relaxed mt-4 text-[#C2BFB9] max-w-xs">
            Practical guides, comparisons and planning tools for the significant decisions homeowners
            actually have to make.
          </p>
          <SocialLinks />
        </div>
        <div>
          <div className="eyebrow eyebrow-light mb-4">Explore categories</div>
          {HUBS.map((h) => (
            <Link
              key={h.slug}
              to={`/hub/${h.slug}`}
              data-testid={`footer-hub-${h.slug}`}
              className="block text-[14.5px] py-1.5 text-[#DAD8D3] hover:text-white transition-colors"
            >
              {h.name}
            </Link>
          ))}
          {/* Medical & Mobility is a separate division with its own navigation: this footer entry
              is its only link from the main site, by request. */}
          <Link
            to="/medical-mobility"
            data-testid="footer-medical-mobility"
            className="block text-[14.5px] py-1.5 text-[#DAD8D3] hover:text-white transition-colors"
          >
            Medical &amp; Mobility
          </Link>
        </div>
        {PLANNING_TOOLS_VISIBLE && (
          <div>
            <div className="eyebrow eyebrow-light mb-4">Planning tools</div>
            {[
              ["golf-simulator-room-fit", "Golf Simulator Room Fit"],
              ["generator-coverage-estimator", "Generator Coverage"],
            ].map(([slug, label]) => (
              <Link
                key={slug}
                to={`/tools/${slug}`}
                className="block text-[14.5px] py-1.5 text-[#DAD8D3] hover:text-white transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        )}
        <div>
          <div className="eyebrow eyebrow-light mb-4">Trust</div>
          {[
            ["/about", "About"],
            ["/how-we-help", "How We Help Homeowners Choose"],
            ["/editorial-standards", "Editorial Standards"],
            ["/affiliate-disclosure", "Affiliate Disclosure"],
            ["/privacy", "Privacy Policy"],
            ["/terms", "Terms"],
            ["/contact", "Contact"],
            ["/partners", "Partner With Us"],
          ].map(([to, label]) => (
            <Link
              key={to}
              to={to}
              data-testid={`footer-link-${to.replace("/", "")}`}
              className="block text-[14.5px] py-1.5 text-[#DAD8D3] hover:text-white transition-colors"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>

      <div className="rule bg-white/10 my-10" />
      <div className="flex flex-col md:flex-row justify-between gap-4 text-[13px] text-[#BDBAB4]">
        <p data-testid="footer-disclosure">
          Human Home Living may earn commissions through affiliate partnerships. Those relationships
          do not determine our conclusions.
        </p>
        <p>© {new Date().getFullYear()} Human Home Living</p>
      </div>
    </div>
  </footer>
);
